/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type Locale } from '@/types/i18n';
import { type YextStoreEntity } from '../interface/yext-types';

type PhoneFieldKey = keyof YextStoreEntity;

export type ContactColumn = {
  labelKey:
    | 'label_locations_contact_column1'
    | 'label_locations_contact_column2'
    | 'label_locations_contact_column3';
  fields: PhoneFieldKey[];
};

export type MappedPhoneInfo = {
  field: string;
  phone: string;
  formatted: string | null;
};

export const CONTACT_COLUMNS_BY_LOCALE: Record<Locale, ContactColumn[]> = {
  'en-US': [
    {
      labelKey: 'label_locations_contact_column1',
      fields: ['mainPhone'],
    },
  ],
  'es-MX': [
    {
      labelKey: 'label_locations_contact_column1',
      fields: ['mainPhone'],
    },
    {
      labelKey: 'label_locations_contact_column2',
      fields: ['c_mostradorProfesional', 'c_mostradorProfesional2'],
    },
  ],
  'pt-BR': [
    {
      labelKey: 'label_locations_contact_column1',
      fields: ['mainPhone'],
    },
    {
      labelKey: 'label_locations_contact_column2',
      fields: ['c_brazilTelephoneCommercial'],
    },
    {
      labelKey: 'label_locations_contact_column3',
      fields: ['c_brazilWhatsappCommercial1', 'c_brazilWhatsappCommercial2'],
    },
  ],
};

export function getMappedContactColumns(
  columns: ContactColumn[],
  storeDetails: YextStoreEntity,
  formatPhone: (phone?: string) => string | null,
): Array<{ column: ContactColumn; phoneNumbers: MappedPhoneInfo[] }> {
  return columns
    .map((column) => {
      const phoneNumbers = column.fields.reduce<MappedPhoneInfo[]>((acc, fieldKey) => {
        const phoneValue = storeDetails[fieldKey] as string | undefined;

        if (phoneValue) {
          acc.push({ field: fieldKey, phone: phoneValue, formatted: formatPhone(phoneValue) });
        }
        return acc;
      }, []);

      return { column, phoneNumbers };
    })
    .filter(({ phoneNumbers }) => phoneNumbers.length > 0);
}
